import { useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "../../utils/common/cn";
import { PAGE_SIZE, Pager } from "./pager";

/** One row of the Weighted Average Life table -- an industry, or (as a
 *  subRow) one office within it. Same shape as
 *  risk-pricing-engine's own components/AthenaPricingEngine/wal-table.tsx. */
export interface WalNode {
  id: string;
  segment: string;
  segmentWal: number;
  portfolioWal: number;
  subRows?: WalNode[];
}

const helper = createColumnHelper<WalNode>();

export function WalTable({ rows }: { rows: WalNode[] }) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [offset, setOffset] = useState(0);
  // Page over top-level industries; office sub-rows travel with their parent.
  const pageOffset = Math.min(offset, Math.max(0, Math.floor((rows.length - 1) / PAGE_SIZE) * PAGE_SIZE));
  // Memoized: a fresh array every render reads as "data changed" to TanStack Table, whose automatic
  // expanded-state reset then sets state on every render (infinite loop on the first re-render, e.g. Next).
  const pageRows = useMemo(() => rows.slice(pageOffset, pageOffset + PAGE_SIZE), [rows, pageOffset]);

  const columns: ColumnDef<WalNode, any>[] = [
    helper.display({
      id: "segment",
      header: "Segment",
      cell: ({ row }) => {
        const canExpand = row.getCanExpand();
        return (
          <div className="flex items-center gap-2" style={{ paddingLeft: `${row.depth * 18}px` }}>
            {canExpand ? (
              <button
                type="button"
                onClick={row.getToggleExpandedHandler()}
                aria-label={row.getIsExpanded() ? "Collapse Row" : "Expand Row"}
                className="flex size-5 items-center justify-center border border-border text-muted-foreground hover:text-foreground"
              >
                {row.getIsExpanded() ? (
                  <ChevronDown className="size-3" />
                ) : (
                  <ChevronRight className="size-3" />
                )}
              </button>
            ) : (
              <span className="size-5" />
            )}
            <span
              className={cn(
                "text-sm",
                row.depth === 0 ? "font-medium text-foreground" : "text-foreground/90",
              )}
            >
              {row.original.segment}
            </span>
          </div>
        );
      },
    }),
    helper.accessor("segmentWal", {
      header: "Segment WAL",
      cell: (info) => info.getValue().toFixed(2),
    }),
    helper.accessor("portfolioWal", {
      header: "Portfolio WAL",
      cell: (info) => (
        <span className="text-muted-foreground">{info.getValue().toFixed(2)}</span>
      ),
    }),
  ];

  const table = useReactTable({
    data: pageRows,
    columns,
    state: { expanded },
    onExpandedChange: setExpanded,
    autoResetExpanded: false,
    getSubRows: (row) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  return (
    <>
    <div className="overflow-x-auto border border-border bg-card">
      <table className="w-full border-collapse text-sm">
        <thead>
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id} className="border-b border-border bg-secondary/50">
              {group.headers.map((header, i) => (
                <th
                  key={header.id}
                  className={cn(
                    "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
                    i === 0 ? "text-left" : "text-right",
                  )}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="border-b border-border/60 last:border-b-0">
              {row.getVisibleCells().map((cell, i) => (
                <td
                  key={cell.id}
                  className={cn("px-3 py-2.5 tabular-nums", i === 0 ? "" : "text-right")}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <Pager offset={pageOffset} total={rows.length} onChange={setOffset} />
    </>
  );
}
